import React from 'react';
import { Edit, Trash2, Users } from 'lucide-react';
import styled from 'styled-components';
import InfoTooltip from 'components/_UI/InfoTooltip';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import fonts from 'theme/fonts';

const STATUS_MAP = {
    accepted: { label: 'Aceito', bg: '#DCFCE7', color: '#166534' },
    pending: { label: 'Pendente', bg: '#FEF3C7', color: '#92400E' },
    rejected: { label: 'Recusado', bg: '#FEE2E2', color: '#DC2626' },
};

/**
 * Quem tem acesso ao evento, no mesmo formato da tabela de pedidos. Cada linha
 * é `{ id, email, permissions, origin, status, onEdit?, onDelete? }`; `origin`
 * é a equipe que dá o acesso, ou `null` quando é um convite direto. Linhas de
 * equipe não têm ações — o acesso delas se gerencia na equipe.
 */
export default function EventAccessTable({ rows }) {
    function renderOrigin(row) {
        if (!row.origin) {
            return <OriginText>Convite direto</OriginText>;
        }

        return (
            <OriginTeam title={row.origin}>
                <Users size={14} color={colors.night} />
                <span>{row.origin}</span>
            </OriginTeam>
        );
    }

    function renderActions(row) {
        if (!row.onEdit && !row.onDelete) {
            return null;
        }

        return (
            <Actions>
                {row.onEdit && (
                    <InfoTooltip text='Editar' type='none'>
                        <ActionButton onClick={row.onEdit} disabled={row.status === 'rejected'}>
                            <Edit color={row.status !== 'rejected' ? colors.primary : colors.nightLight} size={18} />
                        </ActionButton>
                    </InfoTooltip>
                )}
                {row.onDelete && (
                    <InfoTooltip text='Excluir' type='none'>
                        <ActionButton onClick={row.onDelete}>
                            <Trash2 color={colors.danger} size={18} />
                        </ActionButton>
                    </InfoTooltip>
                )}
            </Actions>
        );
    }

    return (
        <Wrapper>
            <DesktopTable>
                <thead>
                    <tr>
                        <Th>Colaborador</Th>
                        <Th>Permissões</Th>
                        <Th>Origem do acesso</Th>
                        <Th>Status</Th>
                        <Th align='right'>Ações</Th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(row => {
                        const statusInfo = STATUS_MAP[row.status] || STATUS_MAP.pending;

                        return (
                            <Tr key={row.id}>
                                <Td><Name title={row.email}>{row.email}</Name></Td>
                                <Td><Secondary>{row.permissions || '—'}</Secondary></Td>
                                <Td>{renderOrigin(row)}</Td>
                                <Td>
                                    <StatusBadge bgColor={statusInfo.bg} textColor={statusInfo.color}>
                                        {statusInfo.label}
                                    </StatusBadge>
                                </Td>
                                <Td align='right'>{renderActions(row)}</Td>
                            </Tr>
                        );
                    })}
                </tbody>
            </DesktopTable>

            <MobileCards>
                {rows.map(row => {
                    const statusInfo = STATUS_MAP[row.status] || STATUS_MAP.pending;

                    return (
                        <MobileCard key={row.id}>
                            <MobileCardHeader>
                                <Name title={row.email}>{row.email}</Name>
                                <StatusBadge bgColor={statusInfo.bg} textColor={statusInfo.color}>
                                    {statusInfo.label}
                                </StatusBadge>
                            </MobileCardHeader>

                            <MobileCardBody>
                                <MobileField>
                                    <MobileLabel>Permissões</MobileLabel>
                                    <MobileValue>{row.permissions || '—'}</MobileValue>
                                </MobileField>
                                <MobileField>
                                    <MobileLabel>Origem</MobileLabel>
                                    {renderOrigin(row)}
                                </MobileField>
                                {renderActions(row) && (
                                    <MobileField>
                                        <MobileLabel>Ações</MobileLabel>
                                        {renderActions(row)}
                                    </MobileField>
                                )}
                            </MobileCardBody>
                        </MobileCard>
                    );
                })}
            </MobileCards>
        </Wrapper>
    );
}

const Wrapper = styled.div`
    border: 1px solid ${colors.neutralDark};
    border-radius: 12px;
    overflow: hidden;
    background: ${colors.neutralLight};
    margin-top: 10px;
`;

const DesktopTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    display: none;

    @media screen and (min-width: ${breakpoints.lg}) {
        display: table;
    }
`;

const Th = styled.th`
    text-align: ${({ align }) => align || 'left'};
    padding: 14px 16px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.night};
    text-transform: uppercase;
    letter-spacing: 0.4px;
    border-bottom: 1px solid ${colors.neutralDark};
    white-space: nowrap;

    &:first-child {
        padding-left: 24px;
    }

    &:last-child {
        padding-right: 24px;
    }
`;

const Tr = styled.tr`
    transition: background 0.15s ease;

    &:not(:last-child) {
        border-bottom: 1px solid ${colors.neutralDark};
    }

    &:hover {
        background: ${colors.neutral};
    }
`;

const Td = styled.td`
    padding: 14px 16px;
    vertical-align: middle;
    text-align: ${({ align }) => align || 'left'};

    &:first-child {
        padding-left: 24px;
    }

    &:last-child {
        padding-right: 24px;
    }
`;

const Name = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
    word-break: break-all;
`;

const Secondary = styled.span`
    font-size: 13px;
    color: ${colors.night};
`;

const OriginText = styled.span`
    font-size: 13px;
    color: ${colors.nightDark};
`;

const OriginTeam = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: ${colors.nightDark};
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    border-radius: 50px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    letter-spacing: 0.2px;
    white-space: nowrap;
    background: ${({ bgColor }) => bgColor};
    color: ${({ textColor }) => textColor};
`;

const Actions = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 12px;
`;

const ActionButton = styled.div`
    transition: all ease-in-out .25s;
    cursor: pointer;
    display: flex;

    ${({ disabled }) => disabled && `
        opacity: .4;
        pointer-events: none;
    `};
`;

const MobileCards = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px 16px;

    @media screen and (min-width: ${breakpoints.lg}) {
        display: none;
    }
`;

const MobileCard = styled.div`
    border: 1px solid ${colors.neutralDark};
    border-radius: 10px;
    padding: 16px;
`;

const MobileCardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid ${colors.neutralDark};
`;

const MobileCardBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const MobileField = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
`;

const MobileLabel = styled.span`
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    color: ${colors.night};
`;

const MobileValue = styled.span`
    font-size: 13px;
    color: ${colors.nightDark};
    text-align: right;
`;
